import { useEffect, useState } from "react";
import { Plus, Trash2, Download, ShoppingCart, Wallet, TrendingUp, TrendingDown, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, formatRupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const today = () => new Date().toISOString().slice(0, 10);

function KasCard({ label, value, tone, testId }) {
  const tones = {
    blue: "bg-[#E5F0FA] text-[#0060AF]",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    slate: "bg-slate-100 text-slate-600",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-2 font-heading text-xl font-bold tracking-tight tabular-nums ${tone === "rose" ? "text-rose-600" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

export default function Expenses() {
  const { user, schoolId } = useAuth();
  const canEdit = ["super_admin", "bendahara"].includes(user.role);
  const [kas, setKas] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [incomes, setIncomes] = useState(null);
  const [needsSchool, setNeedsSchool] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [incOpen, setIncOpen] = useState(false);
  const [expForm, setExpForm] = useState({ date: today(), item_name: "", price: "", qty: "1", note: "" });
  const [receipt, setReceipt] = useState(null);
  const [incForm, setIncForm] = useState({ date: today(), title: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // {kind: 'expense'|'income', item}

  const load = () => {
    setNeedsSchool(false);
    const onErr = (e) => {
      if (e.response?.status === 400) setNeedsSchool(true);
      else toast.error(apiError(e));
    };
    api.get("/kas/summary").then((r) => setKas(r.data)).catch((e) => { setKas(null); onErr(e); });
    api.get("/expenses").then((r) => setExpenses(r.data)).catch(() => setExpenses([]));
    api.get("/other-income").then((r) => setIncomes(r.data)).catch(() => setIncomes([]));
  };

  useEffect(() => {
    if (user.role === "super_admin" && !schoolId) { setNeedsSchool(true); return; }
    load();
  }, [schoolId]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("date", expForm.date);
      fd.append("item_name", expForm.item_name);
      fd.append("price", expForm.price);
      fd.append("qty", expForm.qty);
      fd.append("note", expForm.note);
      if (receipt) fd.append("receipt", receipt);
      await api.post("/expenses", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Belanja berhasil dicatat");
      setExpOpen(false);
      setExpForm({ date: today(), item_name: "", price: "", qty: "1", note: "" });
      setReceipt(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleAddIncome = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/other-income", { ...incForm, amount: Number(incForm.amount) });
      toast.success("Pemasukan lainnya berhasil dicatat");
      setIncOpen(false);
      setIncForm({ date: today(), title: "", amount: "", note: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const url = deleting.kind === "expense" ? `/expenses/${deleting.item.id}` : `/other-income/${deleting.item.id}`;
      await api.delete(url);
      toast.success("Data dihapus");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/export/kas", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "rekap_kas_sekolah.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rekap kas berhasil diunduh");
    } catch {
      toast.error("Gagal mengunduh rekap kas");
    }
  };

  if (needsSchool) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="kas-need-school">
        <p className="font-heading text-lg font-semibold text-slate-800">Pilih sekolah terlebih dahulu</p>
        <p className="mt-1 text-sm text-slate-500">Gunakan pemilih sekolah di pojok kiri atas.</p>
      </div>
    );
  }

  const expTotal = Number(expForm.price || 0) * Number(expForm.qty || 0);
  const fileBase = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
  const token = localStorage.getItem("sb_token");

  return (
    <div className="space-y-6" data-testid="kas-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Kas & Belanja Sekolah</h1>
          <p className="mt-1 text-sm text-slate-500">Saldo kas berasal dari SPP, tagihan, dan pemasukan lainnya dikurangi belanja.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="kas-export-button" className="gap-2 rounded-md">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Ekspor Excel
          </Button>
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setIncOpen(true)} data-testid="add-income-button" className="gap-2 rounded-md border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                <TrendingUp className="h-4 w-4" strokeWidth={1.5} /> Pemasukan Lainnya
              </Button>
              <Button onClick={() => setExpOpen(true)} data-testid="add-expense-button" className="gap-2 rounded-md bg-[#0060AF] hover:bg-[#004B87] active:scale-[0.98]">
                <ShoppingCart className="h-4 w-4" strokeWidth={1.5} /> Catat Belanja
              </Button>
            </>
          )}
        </div>
      </div>

      {kas && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="kas-summary">
          <KasCard label="SPP" value={formatRupiah(kas.spp)} testId="kas-spp" />
          <KasCard label="Tagihan" value={formatRupiah(kas.tagihan)} testId="kas-tagihan" />
          <KasCard label="Lainnya" value={formatRupiah(kas.lainnya)} testId="kas-lainnya" />
          <KasCard label="Belanja" value={formatRupiah(kas.belanja)} tone="rose" testId="kas-belanja" />
          <div className="col-span-2 rounded-xl bg-gradient-to-br from-[#0060AF] to-[#004B87] p-5 text-white" data-testid="kas-saldo">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-200">Saldo Kas Sekolah</p>
            <p className="mt-2 font-heading text-2xl font-bold tracking-tight tabular-nums">{formatRupiah(kas.saldo)}</p>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-slate-800">Belanja Sekolah</h3>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="expenses-table">
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Nama Barang</TableHead>
                <TableHead className="text-right">Harga</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Nota</TableHead>
                <TableHead>Dicatat Oleh</TableHead>
                {canEdit && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses === null && (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-slate-500">Memuat data...</TableCell></TableRow>
              )}
              {expenses !== null && expenses.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-slate-500">Belum ada belanja tercatat.</TableCell></TableRow>
              )}
              {(expenses || []).map((e) => (
                <TableRow key={e.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                  <TableCell className="text-sm text-slate-600">{e.date}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{e.item_name}</TableCell>
                  <TableCell className="text-right text-sm text-slate-700">{formatRupiah(e.price)}</TableCell>
                  <TableCell className="text-right text-sm text-slate-700">{e.qty}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-slate-900">{formatRupiah(e.total)}</TableCell>
                  <TableCell>
                    {e.receipt_path ? (
                      <a href={`${fileBase}/${e.receipt_path}?auth=${token}`} target="_blank" rel="noreferrer"
                         data-testid={`expense-receipt-${e.id}`} className="inline-flex items-center gap-1 text-sm text-[#0060AF] hover:underline">
                        <FileText className="h-3.5 w-3.5" strokeWidth={1.5} /> Lihat
                      </a>
                    ) : <span className="text-sm text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{e.recorded_by}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setDeleting({ kind: "expense", item: e })} data-testid={`delete-expense-${e.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center gap-2">
          <Wallet className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
          <h3 className="font-heading text-base font-semibold text-slate-800">Pemasukan Lainnya</h3>
        </div>
        <div className="mt-4 overflow-x-auto">
          <Table data-testid="income-table">
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Judul</TableHead>
                <TableHead className="text-right">Jumlah</TableHead>
                <TableHead>Catatan</TableHead>
                <TableHead>Dicatat Oleh</TableHead>
                {canEdit && <TableHead className="text-right">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {incomes !== null && incomes.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-slate-500">Belum ada pemasukan lainnya.</TableCell></TableRow>
              )}
              {(incomes || []).map((t) => (
                <TableRow key={t.id} className="transition-colors duration-200 hover:bg-slate-50" data-testid={`income-row-${t.id}`}>
                  <TableCell className="text-sm text-slate-600">{t.date}</TableCell>
                  <TableCell className="text-sm font-medium text-slate-800">{t.title}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-slate-900">{formatRupiah(t.amount)}</TableCell>
                  <TableCell className="text-sm text-slate-600">{t.note || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-600">{t.recorded_by}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setDeleting({ kind: "income", item: t })} data-testid={`delete-income-${t.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent aria-describedby={undefined} data-testid="expense-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catat Belanja Sekolah</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddExpense} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ex-date">Tanggal</Label>
              <Input id="ex-date" type="date" data-testid="expense-date-input" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-name">Nama Barang</Label>
              <Input id="ex-name" data-testid="expense-name-input" value={expForm.item_name} onChange={(e) => setExpForm({ ...expForm, item_name: e.target.value })} required placeholder="Contoh: Buku tulis" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="ex-price">Harga Satuan (Rp)</Label>
                <Input id="ex-price" type="number" min="1" data-testid="expense-price-input" value={expForm.price} onChange={(e) => setExpForm({ ...expForm, price: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ex-qty">Qty</Label>
                <Input id="ex-qty" type="number" min="1" data-testid="expense-qty-input" value={expForm.qty} onChange={(e) => setExpForm({ ...expForm, qty: e.target.value })} required />
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 px-4 py-2.5 text-sm">
              Total: <span className="font-semibold text-slate-900" data-testid="expense-total-preview">{formatRupiah(expTotal)}</span>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-note">Catatan (opsional)</Label>
              <Input id="ex-note" data-testid="expense-note-input" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ex-receipt">Upload Nota (opsional, jpg/png/webp/pdf maks 5MB)</Label>
              <Input id="ex-receipt" type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" data-testid="expense-receipt-input" onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setExpOpen(false)} data-testid="expense-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="expense-form-submit" className="bg-[#0060AF] hover:bg-[#004B87]">
                {saving ? "Menyimpan..." : "Simpan Belanja"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={incOpen} onOpenChange={setIncOpen}>
        <DialogContent aria-describedby={undefined} data-testid="income-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Catat Pemasukan Lainnya</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddIncome} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="in-date">Tanggal</Label>
              <Input id="in-date" type="date" data-testid="income-date-input" value={incForm.date} onChange={(e) => setIncForm({ ...incForm, date: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="in-title">Judul Pemasukan</Label>
              <Input id="in-title" data-testid="income-title-input" value={incForm.title} onChange={(e) => setIncForm({ ...incForm, title: e.target.value })} required placeholder="Contoh: Donasi alumni" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="in-amount">Jumlah (Rp)</Label>
              <Input id="in-amount" type="number" min="1" data-testid="income-amount-input" value={incForm.amount} onChange={(e) => setIncForm({ ...incForm, amount: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="in-note">Catatan (opsional)</Label>
              <Input id="in-note" data-testid="income-note-input" value={incForm.note} onChange={(e) => setIncForm({ ...incForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIncOpen(false)} data-testid="income-form-cancel">Batal</Button>
              <Button type="submit" disabled={saving} data-testid="income-form-submit" className="bg-emerald-600 hover:bg-emerald-700">
                {saving ? "Menyimpan..." : "Simpan Pemasukan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={() => setDeleting(null)}
        title={deleting?.kind === "expense" ? "Hapus Belanja" : "Hapus Pemasukan"}
        description={deleting?.kind === "expense"
          ? `Yakin ingin menghapus belanja "${deleting?.item?.item_name}" senilai ${formatRupiah(deleting?.item?.total)}?`
          : `Yakin ingin menghapus pemasukan "${deleting?.item?.title}" senilai ${formatRupiah(deleting?.item?.amount)}?`}
        onConfirm={handleDelete}
        testId="confirm-delete-kas"
      />
    </div>
  );
}
